import React, { useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatCorrectedAge } from "../../config/age";
import { pathologyName } from "../../config/pathologies";
import { toast } from "react-toastify";
import { API_URL, pathTherapistHome } from "../../config/constants";
import avatarMap from "../../assets/avatars";
import AppSidebar from "../../components/AppSidebar";
import TopBar from "../../components/TopBar";
import AddPatientModal from "../../components/addPatientModal";
import DeletePatientModal from "../../components/deletePatientModal";
import {
  Layout, Main, ContentWrapper, PageTitle,
  Card, UserHeader, UserAvatar, UserId,
  FieldGrid, FieldItem, FieldLabel, FieldValue,
  ActionBar, EditButton, DeleteButton,
} from "./styles";

// 'YYYY-MM-DD' → 'DD/MM/YYYY' sin pasar por Date (evita desfases de zona horaria).
const fmtDate = (d) => {
  if (!/^\d{4}-\d{2}-\d{2}/.test(d || "")) return "—";
  return d.slice(0, 10).split("-").reverse().join("/");
};

const PatientProfile = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const token = localStorage.getItem("token");

  const [patient, setPatient] = useState(location.state?.patient || null);
  const [loading, setLoading] = useState(!location.state?.patient);
  const [deleting, setDeleting] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const loadPatient = () => {
    fetch(`${API_URL}/patients/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => { if (!data.error) setPatient(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPatient(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/patients/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("delete failed");
      toast.success(t("deletePatient.toastDeleted", { code: patient.patient_code }));
      history.push(pathTherapistHome);
    } catch {
      toast.error(t("deletePatient.toastError"));
      setDeleting(false);
      setShowDelete(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <TopBar />
        <AppSidebar patient={null} activeNav="profile" />
        <Main><p>{t("common.loading")}</p></Main>
      </Layout>
    );
  }

  if (!patient) {
    return (
      <Layout>
        <TopBar />
        <AppSidebar patient={null} activeNav="profile" />
        <Main><p>{t("profile.notFound")}</p></Main>
      </Layout>
    );
  }

  return (
    <Layout>
      <TopBar />
      <AppSidebar patient={patient} activeNav="profile" />
      <Main>
        <ContentWrapper>
        <PageTitle>{t("profile.title")}</PageTitle>
        <Card>
          <UserHeader>
            <UserAvatar
              src={avatarMap[patient.avatar_url] || Object.values(avatarMap)[0]}
              alt="avatar"
            />
            <UserId>
              {t("profile.userId")}: <span>{patient.patient_code}</span>
            </UserId>
          </UserHeader>

          <FieldGrid>
            <FieldItem>
              <FieldLabel>{t("addPatient.sex")}</FieldLabel>
              <FieldValue>{patient.sex || "—"}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.treatmentStartDate")}</FieldLabel>
              <FieldValue>{fmtDate(patient.treatment_start_date)}</FieldValue>
            </FieldItem>

            <FieldItem>
              <FieldLabel>{t("addPatient.dateOfBirth")}</FieldLabel>
              <FieldValue>{fmtDate(patient.date_of_birth)}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.ageAtStart")}</FieldLabel>
              <FieldValue>{formatCorrectedAge(i18n.language, patient) || "—"}</FieldValue>
            </FieldItem>

            <FieldItem>
              <FieldLabel>{t("addPatient.isPremature")}</FieldLabel>
              <FieldValue>{patient.is_premature ? t("profile.yes") : t("profile.no")}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.assignedTherapist")}</FieldLabel>
              <FieldValue>{patient.assigned_therapist_name || "—"}</FieldValue>
            </FieldItem>

            {patient.is_premature && (
              <FieldItem>
                <FieldLabel>{t("addPatient.expectedDelivery")}</FieldLabel>
                <FieldValue>{fmtDate(patient.expected_delivery_date)}</FieldValue>
              </FieldItem>
            )}
            <FieldItem>
              <FieldLabel>{t("addPatient.pathology")}</FieldLabel>
              <FieldValue>{pathologyName(t, patient.pathology_id, patient.pathology) || "—"}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.secondaryDiagnosis")}</FieldLabel>
              <FieldValue>{patient.secondary_diagnosis || "—"}</FieldValue>
            </FieldItem>

            <FieldItem>
              <FieldLabel>{t("addPatient.cohort")}</FieldLabel>
              <FieldValue>{patient.cohort ?? "—"}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>{t("addPatient.notes")}</FieldLabel>
              <FieldValue>{patient.notes_observations || "—"}</FieldValue>
            </FieldItem>
          </FieldGrid>

          <ActionBar>
            <EditButton type="button" onClick={() => setShowEdit(true)}>{t("profile.editPatient")}</EditButton>
            <DeleteButton type="button" onClick={() => setShowDelete(true)} disabled={deleting}>
              {t("profile.deletePatient")}
            </DeleteButton>
          </ActionBar>
        </Card>
        </ContentWrapper>
      </Main>
      {showEdit && (
        <AddPatientModal
          patient={patient}
          onClose={() => setShowEdit(false)}
          onUpdated={() => { setShowEdit(false); setLoading(true); loadPatient(); }}
        />
      )}

      {showDelete && (
        <DeletePatientModal
          patientCode={patient.patient_code}
          loading={deleting}
          onCancel={() => setShowDelete(false)}
          onConfirm={handleDelete}
        />
      )}
    </Layout>
  );
};

export default PatientProfile;
